/**
 * Icons8 3D Fluency Icon Component
 * Beautiful 3D colored icons for the Aegis platform
 * 
 * Browse icons: https://icons8.com/icons/set/3d-fluency
 */

import React from "react";
import { cn } from "@/lib/utils";

// Map of commonly used icons to Icons8 3D Fluency names
const ICON_MAP: Record<string, string> = {
  // Navigation & Actions
  "arrow-right": "arrow-right",
  "arrow-left": "arrow-left",
  "arrow-forward": "forward",
  "chevron-right": "chevron-right",
  "chevron-left": "chevron-left",
  "chevron-down": "expand-arrow",
  "chevron-up": "collapse-arrow",
  "menu": "menu",
  "more": "more",
  "more-horizontal": "more",
  "more-vertical": "more",
  "x": "delete-sign",
  "close": "delete-sign",
  "x-circle": "cancel",
  "check": "checkmark",
  "checkmark": "checkmark",
  "check-circle": "ok",
  "checked": "ok",
  "plus": "plus",
  "minus": "minus",
  "search": "search",
  "filter": "filter",
  "refresh": "synchronize",
  "reload": "synchronize",
  "sync": "synchronize",
  "synchronize": "synchronize",
  "external-link": "external-link",
  "link": "link",
  "copy": "copy",
  "trash": "trash",
  "trash2": "trash",
  "trash-2": "trash",
  "edit": "edit",
  "pencil": "pencil",
  "save": "save",
  "download": "download",
  "upload": "upload",
  "share": "share",
  "send": "sent",
  "undo": "undo",
  "redo": "redo",
  "cancel": "cancel",
  "log-out": "exit",
  "logout": "exit",
  "grid": "grid",
  "list": "list",
  "circle": "circle",
  
  // Security & Privacy
  "shield": "shield",
  "shield-check": "verified-account",
  "lock": "lock",
  "unlock": "unlock",
  "key": "key",
  "fingerprint": "fingerprint",
  "eye": "eye",
  "eye-off": "hide",
  "visible": "eye",
  "invisible": "hide",
  "scan": "barcode-scanner",
  
  // Users & People
  "user": "user",
  "users": "conference",
  "crowd": "conference",
  "user-plus": "add-user-male",
  "user-check": "checked-user-male",
  "user-x": "remove-user-male",
  "contact": "contact-card",
  "heart": "heart",
  "star": "star",
  "handshake": "handshake",
  
  // Documents & Files
  "file": "document",
  "file-text": "document",
  "documents": "documents",
  "file-check": "approval",
  "folder": "folder",
  "folder-open": "opened-folder",
  "archive": "archive",
  "clipboard": "clipboard",
  "book": "book",
  "bookmark": "bookmark",
  
  // Communication
  "mail": "new-post",
  "email": "new-post",
  "message": "speech-bubble",
  "sms": "speech-bubble",
  "chat": "chat",
  "bell": "notification",
  "phone": "phone",
  "video": "video-call",
  "megaphone": "advertising",
  
  // Business & Finance
  "briefcase": "briefcase",
  "building": "organization",
  "building-2": "company",
  "bank": "bank-building",
  "museum": "bank-building",
  "credit-card": "bank-card-back-side",
  "dollar": "us-dollar",
  "wallet": "wallet",
  "chart": "combo-chart",
  "bar-chart": "bar-chart",
  "trending-up": "positive-dynamic",
  "trending-down": "negative-dynamic",
  "pie-chart": "doughnut-chart",
  "receipt": "bill",
  "scales": "scales",
  
  // Technology
  "settings": "settings",
  "cog": "settings",
  "gear": "settings",
  "code": "code",
  "terminal": "console",
  "database": "database",
  "server": "server",
  "cloud": "cloud",
  "wifi": "wi-fi-connected",
  "bluetooth": "bluetooth",
  "smartphone": "iphone",
  "iphone": "iphone",
  "laptop": "laptop",
  "monitor": "monitor",
  "tablet": "ipad",
  "cpu": "processor",
  "hard-drive": "ssd",
  "usb": "usb-connector",
  "globe": "globe",
  "world": "globe",
  "webhook": "webhook",
  "api": "api",
  
  // Time & Calendar
  "clock": "clock",
  "calendar": "calendar",
  "timer": "stopwatch",
  "hourglass": "hourglass",
  "alarm": "alarm-clock",
  "history": "time-machine",
  
  // Status & Alerts
  "alert": "error",
  "alert-circle": "error",
  "alert-triangle": "high-priority",
  "info": "info",
  "help": "help",
  "help-circle": "help",
  "question": "question-mark",
  "warning": "high-priority",
  "error": "cancel",
  "success": "ok",
  "loader": "loading",
  "spinner": "loading",
  
  // Media
  "image": "image",
  "camera": "camera",
  "music": "music",
  "video-camera": "video-camera",
  "mic": "microphone",
  "volume": "speaker",
  "play": "play",
  "pause": "pause",
  "stop": "stop",
  
  // Health & Safety
  "medical": "medical-doctor",
  "hospital": "hospital",
  "pill": "pill",
  "activity": "health-graph",
  "heart-pulse": "heart-with-pulse",
  "stethoscope": "stethoscope",
  "ambulance": "ambulance",
  
  // Legal & Compliance
  "scale": "scales",
  "gavel": "law",
  "certificate": "certificate",
  "stamp": "postage-stamp",
  "signature": "signature",
  
  // Nature & Weather
  "sun": "sun",
  "moon": "crescent-moon",
  "cloud-sun": "partly-cloudy-day",
  "rain": "rain",
  "snow": "snow",
  "lightning": "lightning-bolt",
  "tree": "deciduous-tree",
  "leaf": "leaf",
  
  // Travel & Location
  "map": "map",
  "map-pin": "place-marker",
  "location": "place-marker",
  "marker": "place-marker",
  "compass": "compass",
  "plane": "airplane-take-off",
  "car": "car",
  "home": "home",
  "flag": "flag",
  
  // Shopping & Commerce
  "cart": "shopping-cart",
  "bag": "shopping-bag",
  "tag": "price-tag",
  "gift": "gift",
  "package": "open-parcel",
  "box": "box",
  
  // Social
  "thumbs-up": "thumbs-up",
  "thumbs-down": "thumbs-down",
  "smile": "smiling-face",
  "emoji": "happy",
  
  // Misc
  "zap": "lightning-bolt",
  "fire": "fire",
  "sparkles": "sparkling",
  "magic": "magic-wand",
  "puzzle": "puzzle",
  "target": "target",
  "trophy": "trophy",
  "crown": "crown",
  "rocket": "rocket",
  "lightbulb": "idea",
  "brain": "brain",
  "cookie": "cookie",
  "coffee": "coffee-to-go",
  "pizza": "pizza",
  
  // Aegis-specific
  "continuity": "infinity",
  "legacy": "family",
  "inheritance": "inheritance",
  "vault": "safe",
  "capsule": "time-capsule",
  "proof": "approval",
  "witness": "binoculars",
  "protocol": "flow-chart",
  "trigger": "switch-on",
  "dependent": "children",
  "executor": "businessman",
  "beneficiary": "receive-cash",
};

// Size presets
const SIZE_MAP = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
  xl: 48,
  "2xl": 64,
  "3xl": 94,
};

type IconSize = keyof typeof SIZE_MAP | number;

interface Icon3DProps {
  name: string;
  size?: IconSize;
  className?: string;
  alt?: string;
  fallback?: React.ReactNode;
}

/**
 * Get the Icons8 CDN URL for a 3D Fluency icon
 */
function getIconUrl(iconName: string, size: number): string {
  const mappedName = ICON_MAP[iconName.toLowerCase()] || iconName;
  // Use the closest size that Icons8 supports (94 is high quality)
  const iconSize = size <= 24 ? 48 : size <= 48 ? 94 : 188;
  return `https://img.icons8.com/3d-fluency/${iconSize}/${mappedName}.png`;
}

/**
 * Get a fallback SVG for check-related icons
 */
function getFallbackCheckIcon(name: string, size: number): React.ReactNode {
  const lowerName = name.toLowerCase();
  
  // Check icons - show checkmark
  if (lowerName.includes('check')) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="inline-block flex-shrink-0"
      >
        {lowerName.includes('circle') ? (
          <>
            <circle cx="12" cy="12" r="10" />
            <polyline points="16 12 12 8 8 12" />
          </>
        ) : (
          <polyline points="20 6 9 17 4 12" />
        )}
      </svg>
    );
  }
  
  // Checkmark icon
  if (lowerName.includes('checkmark')) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="inline-block flex-shrink-0"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
    );
  }
  
  // Default - return null to just hide
  return null;
}

/**
 * 3D Fluency Icon Component
 * 
 * @example
 * <Icon3D name="shield" size="lg" />
 * <Icon3D name="user" size={32} className="opacity-80" />
 */
export function Icon3D({ 
  name, 
  size = "md", 
  className,
  alt,
  fallback,
}: Icon3DProps) {
  const pixelSize = typeof size === "number" ? size : SIZE_MAP[size];
  const url = getIconUrl(name, pixelSize);
  const [imageError, setImageError] = React.useState(false);
  
  // Show fallback SVG if image failed to load
  if (imageError) {
    const fallbackSvg = getFallbackCheckIcon(name, pixelSize);
    if (fallbackSvg) {
      return <span className={className}>{fallbackSvg}</span>;
    }
    // If we have a custom fallback, show that
    if (fallback) {
      return <>{fallback}</>;
    }
    // Otherwise, return empty span
    return <span className={cn("inline-block flex-shrink-0", className)} />;
  }
  
  return (
    <img
      src={url}
      alt={alt || name}
      width={pixelSize}
      height={pixelSize}
      className={cn("inline-block flex-shrink-0", className)}
      loading="lazy"
      onError={() => {
        setImageError(true);
      }}
    />
  );
}

/**
 * Icon with text label
 */
interface Icon3DLabelProps extends Icon3DProps {
  label: string;
  labelClassName?: string;
}

export function Icon3DLabel({ 
  label, 
  labelClassName,
  ...iconProps 
}: Icon3DLabelProps) {
  return (
    <span className="inline-flex items-center gap-2">
      <Icon3D {...iconProps} />
      <span className={labelClassName}>{label}</span>
    </span>
  );
}

/**
 * Animated icon with hover effect
 */
export function Icon3DAnimated({ className, ...props }: Icon3DProps) {
  return (
    <Icon3D
      {...props}
      className={cn(
        "transition-all duration-300 ease-out hover:scale-110 hover:rotate-3 hover:brightness-110",
        className
      )}
    />
  );
}

/**
 * Icon button wrapper
 */
interface Icon3DButtonProps extends Icon3DProps {
  onClick?: () => void;
  disabled?: boolean;
}

export function Icon3DButton({ 
  onClick, 
  disabled, 
  className,
  ...iconProps 
}: Icon3DButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center justify-center rounded-lg p-2",
        "hover:bg-accent transition-colors",
        "disabled:opacity-50 disabled:pointer-events-none",
        className
      )}
    >
      <Icon3D {...iconProps} />
    </button>
  );
}

export default Icon3D;
